import { useEffect, useLayoutEffect } from "react";

import { mobile } from "./mobile";

// Module-level, shared across every instance of the hook - callers commonly nest (eg. a share
// modal opened from inside an already-modal related-item view). Without this, the inner modal's
// unmount would restore the tab bar out from under the still-open outer one, producing a visible
// flicker before something else re-hid it. Counting active "wants it hidden" callers means only
// the first hide and the last restore actually touch the bridge.
let hiddenCount = 0;
let unregisterReassertListeners: (() => void) | undefined;

// focusout bubbles to the document for every blur, not just ones that dismiss the keyboard -
// tabbing between fields, clicking into a button, or a programmatic .blur() all fire it. Only a
// text-entry element losing focus can have raised the keyboard in the first place, so gate on
// that to avoid a redundant bridge call on every unrelated blur.
function isTextEntryElement(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    (target instanceof HTMLElement && target.isContentEditable)
  );
}

function hideTabBar(
  setTabBarVisibility: (visible: boolean, animated?: boolean) => void,
) {
  setTabBarVisibility(false, true);

  // Some native shells re-show their own tab bar as part of their keyboard-dismiss handling,
  // silently undoing the call above with nothing to tell the web side it happened. Re-assert
  // hidden whenever that might have occurred so the tab bar snaps back down instead of
  // getting stuck visible once the keyboard closes. Unanimated - this is a correction for an
  // unwanted native animation already in flight, not an intentional transition, so animating
  // our hide on top of it just doubles the visible flicker.
  const reassertHidden = () => setTabBarVisibility(false, false);

  // Android resizes window.visualViewport when the keyboard opens/closes, so a "resize"
  // listener catches the dismiss there. iOS does NOT resize the visual viewport for the
  // keyboard (see useKeyboardSafeViewport.ts) - it shifts the viewport instead - so "resize"
  // alone misses iOS keyboard dismissals. "focusout" bubbles to the document whenever the
  // focused field (the one that raised the keyboard) loses focus, which covers iOS too.
  const reassertHiddenOnFocusOut = (event: FocusEvent) => {
    if (isTextEntryElement(event.target)) {
      reassertHidden();
    }
  };
  const viewport = window.visualViewport;
  viewport?.addEventListener("resize", reassertHidden);
  document.addEventListener("focusout", reassertHiddenOnFocusOut);

  unregisterReassertListeners = () => {
    viewport?.removeEventListener("resize", reassertHidden);
    document.removeEventListener("focusout", reassertHiddenOnFocusOut);
  };
}

/**
 * Takes one "wants it hidden" claim on the tab bar and returns the function that releases it, or
 * `undefined` when there is nothing to hold (not a webview, `hidden` is false, or the bridge
 * lacks the call).
 */
function holdTabBarHidden(hidden: boolean): (() => void) | undefined {
  if (!hidden || !mobile.isWebview) {
    return undefined;
  }

  // Optional on the bridge - older app versions and non-brownfield contexts lack it.
  const setTabBarVisibility = mobile.setTabBarVisibility;
  if (typeof setTabBarVisibility !== "function") {
    return undefined;
  }

  hiddenCount += 1;
  if (hiddenCount === 1) {
    // hideTabBar crosses into the native bridge, which can throw. If it does here, the
    // effect callback throws before React ever gets a cleanup function to register, so
    // the increment above would otherwise leak forever with no caller left to release it.
    try {
      hideTabBar(setTabBarVisibility);
    } catch (error) {
      hiddenCount -= 1;
      throw error;
    }
  }

  return () => {
    // Floored defensively - every real unmount pairs with the increment above, but a
    // stray double-decrement should self-correct instead of driving the count negative
    // and requiring extra hides just to reach zero again.
    hiddenCount = Math.max(0, hiddenCount - 1);
    if (hiddenCount === 0) {
      unregisterReassertListeners?.();
      unregisterReassertListeners = undefined;
      setTabBarVisibility(true, true);
    }
  };
}

/**
 * Hides the native bottom tab bar while `hidden` is true, and restores it when that flips back
 * or the caller unmounts. The cleanup is what covers navigating away with an overlay still open,
 * which would otherwise strand the tab bar hidden.
 *
 * Safe to nest - eg. a share modal opened on top of an already-modal detail view. Only the first
 * concurrent caller actually hides the tab bar, and it's only restored once every caller that
 * wanted it hidden has flipped back or unmounted.
 *
 * Outside a mobile webview this is a no-op, so callers don't need their own `isWebview` guard.
 *
 * The hold is taken in a passive effect (`useEffect`). For UI that mounts as another overlay closes
 * into it, use {@link useHideNativeTabBarLayoutEffect}, the `useLayoutEffect` counterpart.
 *
 * @param hidden Whether an overlay is currently covering the tab bar.
 *
 * @example
 * ```tsx
 * // Keep the tab bar out from under a full-screen modal.
 * useHideNativeTabBar(visible);
 * ```
 */
export function useHideNativeTabBar(hidden: boolean) {
  useEffect(() => holdTabBarHidden(hidden), [hidden]);
}

/**
 * `useLayoutEffect` counterpart of {@link useHideNativeTabBar}: the same hold, taken during the
 * commit instead of after paint. Use it for UI that mounts at the moment an overlay holding the
 * bar closes into it - an inline editor opened from a bottom sheet, say.
 *
 * React runs a commit's layout effects before the passive-effect cleanup of a caller released in
 * that same commit, so the hand-off goes 1 -> 2 -> 1 and the tab bar never moves. With the passive
 * hook it goes 1 -> 0 -> 1 and the bar is restored for an instant, and on iOS showing the bar while
 * the keyboard is up or rising dismisses the keyboard.
 *
 * It relies on the new caller mounting in the same commit as the release. Where it mounts later,
 * the bar still flashes in between.
 *
 * @param hidden Whether the caller wants the bar hidden.
 *
 * @example
 * ```tsx
 * // An inline editor that holds the bar hidden for as long as it is mounted.
 * useHideNativeTabBarLayoutEffect(true);
 * ```
 */
export function useHideNativeTabBarLayoutEffect(hidden: boolean) {
  useLayoutEffect(() => holdTabBarHidden(hidden), [hidden]);
}
